import React from 'react';
import PropTypes from 'prop-types';
import { Grid, CardContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import { useIsBreakpointDown } from '~/modules/common/hooks';
import ResourcingCard from '~/modules/resource-management/ResourcingOverview/components/ResourcingCard';
import { BenchOverAllocationTimelineSkeleton } from './components/BenchOverAllocationTrackerCard/BenchOverAllocationTimeline/BenchOverAllocationTimelineSkeleton';
import BenchOverAllocationChartSkeleton from './components/BenchOverAllocationTrackerCard/BenchOverAllocationDetailsSection/components/BenchOverAllocationChart/BenchOverAllocationChartSkeleton';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(1, 0, 1, 1.5),
    [theme.breakpoints.down('md')]: {
      paddingTop: 0
    }
  },
  trackerContainer: {
    minWidth: 0
  },
  cardContent: {
    padding: 0
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: 0,
    marginBottom: theme.spacing(1)
  },
  toolbarSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  detailsToolbar: {
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 2),
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2)
  },
  detailsToolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  detailsToolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  actionItemsContent: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row'
    }
  },
  actionItem: {
    minWidth: theme.spacing(22.5),
    maxWidth: theme.spacing(22.5),
    padding: theme.spacing(2, 2, 0.25)
  }
}));

const classesPropTypes = {
  classes: PropTypes.object.isRequired
};

const ToolbarSkeleton = ({ classes }) => (
  <div className={classes.toolbar}>
    <div className={classes.toolbarSection}>
      <Skeleton variant="rect" width={200} height={32} />
    </div>
    <div className={classes.toolbarSection}>
      <Skeleton variant="rect" width={100} height={36} />
      <Skeleton variant="rect" width={80} height={36} />
    </div>
  </div>
);

ToolbarSkeleton.propTypes = classesPropTypes;

const DetailsToolbarSkeleton = ({ classes }) => (
  <div className={classes.detailsToolbar}>
    <div className={classes.detailsToolbarLeft}>
      <Skeleton variant="rect" width={120} height={32} />
      <Skeleton variant="rect" width={100} height={32} />
    </div>
    <div className={classes.detailsToolbarRight}>
      <Skeleton variant="rect" width={70} height={32} />
      <Skeleton variant="rect" width={70} height={32} />
    </div>
  </div>
);

DetailsToolbarSkeleton.propTypes = classesPropTypes;

const TrackerCardSkeleton = ({ classes }) => (
  <ResourcingCard title={<Skeleton variant="text" width={250} height={24} />}>
    <CardContent className={classes.cardContent}>
      <BenchOverAllocationTimelineSkeleton />
      <DetailsToolbarSkeleton classes={classes} />
      <BenchOverAllocationChartSkeleton />
    </CardContent>
  </ResourcingCard>
);

TrackerCardSkeleton.propTypes = classesPropTypes;

const ActionItemSkeleton = ({ classes }) => (
  <div className={classes.actionItem}>
    <Skeleton variant="text" width={60} height={32} />
    <Skeleton variant="text" width={120} height={16} />
  </div>
);

ActionItemSkeleton.propTypes = classesPropTypes;

const ActionItemsCardSkeleton = ({ classes }) => (
  <ResourcingCard title={<Skeleton variant="text" width={120} height={24} />}>
    <div className={classes.actionItemsContent}>
      {Array.from({ length: 5 }, (_, i) => (
        <ActionItemSkeleton
          key={`action-item-skeleton-${i}`}
          classes={classes}
        />
      ))}
    </div>
  </ResourcingCard>
);

ActionItemsCardSkeleton.propTypes = classesPropTypes;

const ResourceManagementDashboardInsightsSkeleton = () => {
  const classes = useStyles();
  const isSMView = useIsBreakpointDown('sm');

  return (
    <div className={classes.container}>
      <ToolbarSkeleton classes={classes} />
      <Grid container spacing={2}>
        <Grid item xs={12} md className={classes.trackerContainer}>
          {isSMView && <ActionItemsCardSkeleton classes={classes} />}
          <TrackerCardSkeleton classes={classes} />
        </Grid>
        {!isSMView && (
          <Grid item md="auto">
            <ActionItemsCardSkeleton classes={classes} />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default ResourceManagementDashboardInsightsSkeleton;
